import type { CountdownApiClient } from "@countdown/api-client";
import { FileDrop } from "@countdown/door-editor";
import {
  DEFAULT_DOOR_LAYOUT,
  type DoorPresentation,
  type DoorWindowLayout,
  embedFrame,
  embedShortcode,
} from "@countdown/theme";
import { type ReactElement, useCallback, useId, useMemo, useState } from "react";

import { can } from "@/lib/labels";
import { ConfirmButton } from "@/components/ConfirmButton";
import { CropButton, assetIdOf } from "@/components/CropButton";
import { DoorEditor } from "@/components/DoorEditor";
import { CopyField } from "@/components/EmbedSettings";
import { Notice } from "@/components/Notice";
import { FramedPage } from "@/components/ParticipantPreview";
import { EditorLayout } from "@/components/PreviewLayout";
import { SaveForm } from "@/components/SaveForm";
import { Section } from "@/components/Section";
import { UnscannedAccept } from "@/components/UnscannedAccept";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ActionDetail,
  type ActionOutcome,
  type DoorOutcome,
  type ExampleEdit,
  type ExampleOutcome,
  LOAD_FAILED,
  ROLE_MISSING,
  assetAddress,
  doorOutcome,
  exampleDoor,
  loadActionDetail,
  loadExample,
  mediaSrc,
  saveExampleDoor,
  saveExampleSettings,
  switchExampleLink,
  originalAddress,
  uploadExampleMedia,
  uploadExamplePartnerLogo,
  useResource,
} from "@/lib/api";
import type { DoorWrites } from "@/lib/door";
import { type PartnerRow, partnerInput, partnerRows, settingsRequest } from "@/lib/example";
import { useActionContext } from "@/pages/ActionPage";

/** The answer of a write as the shared buttons and forms take it. */
function asAction(outcome: ExampleOutcome): ActionOutcome {
  return outcome.ok ? { ok: true } : { ok: false, message: outcome.message };
}

/**
 * Tab „Beispielansicht" of an action (`/aktionen/:actionId/beispiel`, D32), for tenant admins
 * and action managers.
 *
 * The example shows prospective sponsors the calendar of this action with its own design, one
 * open door and the completion, behind a link with a random key. Nothing in it is stored, sent
 * or counted; the participant frontend answers every page of it from one read.
 *
 * Every write answers with the whole tab, which becomes the page's state: a save of a door
 * also moves the door its editor holds, and an upload of one partner's logo leaves the names
 * typed into the other rows alone. The link stays switchable after the season, so a link that
 * went too far can still be revoked; door, partners and content freeze with the action.
 *
 * Every door from 1 to the example's day has content of its own (D37) and opens in the
 * example; each is chosen and configured here, in the window the action's layout gives it.
 *
 * Beside the settings the example itself is framed (the participant frontend lets the tenant's
 * backoffice frame `?beispiel=` pages) and loads again after every write.
 */
export function ExamplePage(): ReactElement {
  const { action } = useActionContext();
  const load = useCallback(
    (api: CountdownApiClient) => loadExample(api, action.action_id),
    [action.action_id],
  );
  const loaded = useResource<ExampleEdit>(load);

  if (loaded.status === "forbidden") {
    return <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>;
  }
  if (loaded.status !== "ready") {
    return (
      <p className="text-sm text-muted-foreground">
        {loaded.status === "loading" ? "Wird geladen …" : LOAD_FAILED}
      </p>
    );
  }
  return <LoadedExample initial={loaded.data} />;
}

function LoadedExample({ initial }: { readonly initial: ExampleEdit }): ReactElement {
  const { action, granted } = useActionContext();
  const [view, setView] = useState(initial);
  // Raised by every write, so the example beside the settings loads again and shows it.
  const [frame, setFrame] = useState(0);
  const actionId = action.action_id;
  const ended = action.status === "finished" || action.status === "archived";
  const mayAcceptUnscanned = can(granted, "approve_unscanned");

  /** Takes the answer of a write as the new state of the tab. */
  const took = useCallback((outcome: ExampleOutcome): ExampleOutcome => {
    if (outcome.ok) {
      setView(outcome.view);
      setFrame((count) => count + 1);
    }
    return outcome;
  }, []);

  // The doors 1 to the example's day; the open one is the default choice.
  const doors = (view.doors ?? []).length > 0 ? (view.doors ?? []) : [view.door];
  const [chosen, setChosen] = useState(view.booked_position);
  const position = doors.some((door) => door.field_position === chosen)
    ? chosen
    : view.booked_position;
  // A door's editor stays mounted once opened, so its unsaved edits outlive a look at another
  // door and still join the save bar. A door the example's day no longer reaches drops out:
  // its editor would be handed the open door and save its draft over that one.
  const [opened, setOpened] = useState<readonly number[]>(() => [position]);
  const shown = (opened.includes(position) ? opened : [...opened, position]).filter((at) =>
    doors.some((door) => door.field_position === at),
  );
  const loadDetail = useCallback(
    (api: CountdownApiClient) => loadActionDetail(api, actionId),
    [actionId],
  );
  const detail = useResource<ActionDetail>(loadDetail);
  // The example's doors show the action's layout, the one a sponsor would book.
  const layout: DoorWindowLayout =
    detail.status === "ready" ? (detail.data.door_layout ?? DEFAULT_DOOR_LAYOUT) : DEFAULT_DOOR_LAYOUT;
  const presentation: DoorPresentation =
    detail.status === "ready" ? (detail.data.door_presentation ?? "page") : "page";

  return (
    <EditorLayout
      preview={
        view.enabled && view.url ? (
          <FramedPage src={view.url} title="Beispielansicht" defaultMobile revision={frame} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Die Beispielansicht erscheint hier, sobald ein Link erzeugt ist.
          </p>
        )
      }
    >
      <LinkSection view={view} actionId={actionId} took={took} />

      {ended ? (
        <Notice tone="info" className="mb-6">
          Die Aktion ist beendet. Türchen, Partner und Inhalt lassen sich nicht mehr ändern.
        </Notice>
      ) : (
        <>
          <Section title="Türchen und Partner">
            <PartnerForm
              view={view}
              actionId={actionId}
              fieldCount={action.field_count}
              mayAcceptUnscanned={mayAcceptUnscanned}
              took={took}
            />
          </Section>

          <Section
            title="Inhalt der Türchen"
            description="Jedes Türchen bis zum heutigen hat eigene Folien, ein Quiz und einen Gewinn, wie ein Sponsor sie anlegt. Bilder sind sofort öffentlich."
          >
            <DoorChoice
              doors={doors.map((door) => door.field_position)}
              today={view.booked_position}
              value={position}
              onChange={(next) => {
                setChosen(next);
                setOpened((current) => (current.includes(next) ? current : [...current, next]));
              }}
            />
            {shown.map((at) => (
              <div key={at} hidden={at !== position}>
                <ExampleDoor
                  view={view}
                  position={at}
                  actionId={actionId}
                  mayAcceptUnscanned={mayAcceptUnscanned}
                  layout={layout}
                  presentation={presentation}
                  took={took}
                />
              </div>
            ))}
          </Section>
        </>
      )}
    </EditorLayout>
  );
}

/** The door whose content the tab shows: 1 to the example's day. */
function DoorChoice({
  doors,
  today,
  value,
  onChange,
}: {
  readonly doors: readonly number[];
  readonly today: number;
  readonly value: number;
  readonly onChange: (position: number) => void;
}): ReactElement {
  const id = useId();
  return (
    <div className="mb-4 grid max-w-xs gap-1.5">
      <Label htmlFor={`${id}-tuer`}>Türchen</Label>
      <select
        id={`${id}-tuer`}
        className="h-9 border border-input bg-background px-3 text-sm"
        value={value}
        onChange={(event) => {
          onChange(Number(event.target.value));
        }}
      >
        {doors.map((position) => (
          <option key={position} value={position}>
            Türchen {position}
            {position === today ? " (heute)" : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * One door of the example in the configurator, written to its own place (`position`). The
 * example has no prize stock, no video and no review, so the quantity, the videos and
 * "einreichen" are off; its layout is the action's and only shown.
 */
function ExampleDoor({
  view,
  position,
  actionId,
  mayAcceptUnscanned,
  layout,
  presentation,
  took,
}: {
  readonly view: ExampleEdit;
  readonly position: number;
  readonly actionId: string;
  readonly mayAcceptUnscanned: boolean;
  readonly layout: DoorWindowLayout;
  readonly presentation: DoorPresentation;
  readonly took: (outcome: ExampleOutcome) => ExampleOutcome;
}): ReactElement {
  const door = useMemo(() => exampleDoor(view, position), [view, position]);
  const writes = useMemo<DoorWrites>(
    () => ({
      save: async (request): Promise<DoorOutcome> =>
        doorOutcome(took(await saveExampleDoor(actionId, request)), position),
      upload: async (upload): Promise<DoorOutcome> =>
        doorOutcome(took(await uploadExampleMedia(actionId, { ...upload, position })), position),
      recut: async ({ slot, assetId, crop }): Promise<DoorOutcome> =>
        doorOutcome(
          took(await uploadExampleMedia(actionId, { position, slot, assetId, crop })),
          position,
        ),
      original: originalAddress,
      picture: assetAddress,
    }),
    [actionId, took, position],
  );
  return (
    <>
      {door.quiz.questions.length === 0 ? (
        <Notice tone="info">
          Ohne Frage endet dieses Türchen der Beispielansicht beim Inhalt: Antwort und
          Teilnahmeabschluss fehlen dann.
        </Notice>
      ) : null}
      <DoorEditor
        previewTitle={`Vorschau des Fensters von Türchen ${String(position)}`}
        door={door}
        writes={writes}
        saveLabel={`Türchen ${String(position)}`}
        prizeQuantity={false}
        videos={false}
        acceptUnscanned={mayAcceptUnscanned}
        layout={layout}
        presentation={presentation}
        layoutNote="Aufbau und Darstellung kommen aus der Gestaltung der Aktion, wie bei einem gebuchten Türchen."
      />
    </>
  );
}

/** The link for prospective sponsors: choose, copy, open, change, switch off. */
function LinkSection({
  view,
  actionId,
  took,
}: {
  readonly view: ExampleEdit;
  readonly actionId: string;
  readonly took: (outcome: ExampleOutcome) => ExampleOutcome;
}): ReactElement {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<ActionOutcome | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const url = view.url ?? null;

  // Since D36 the link is the action's own path with `?beispiel`: a switch, no key to choose.
  const switchLink = async (enabled: boolean): Promise<ActionOutcome> => {
    setCopied(null);
    return asAction(took(await switchExampleLink(actionId, enabled, "")));
  };

  return (
    <Section
      title="Link"
      description="Zeigt Interessenten den Kalender dieser Aktion mit ihrer Gestaltung, einem geöffneten Türchen und dem Teilnahmeabschluss. Dabei wird nichts gespeichert, keine E-Mail verschickt und nichts gezählt."
    >
      {view.enabled ? null : (
        <div className="grid justify-items-start gap-3">
          <p className="text-sm">
            Die Beispielansicht ist abgeschaltet und nicht erreichbar.
          </p>
          <Button
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setSaid(null);
              switchLink(true)
                .then((outcome) => {
                  setSaid(outcome.ok ? null : outcome);
                })
                .catch((error: unknown) => {
                  console.error("[ExamplePage] link failed", error);
                  setSaid({ ok: false, message: LOAD_FAILED });
                })
                .finally(() => {
                  setBusy(false);
                });
            }}
          >
            {busy ? "Wird ausgeführt …" : "Beispielansicht einschalten"}
          </Button>
          {said !== null && !said.ok ? <Notice tone="error">{said.message}</Notice> : null}
        </div>
      )}

      {view.enabled && url === null ? (
        <Notice tone="info">Für diesen Mandanten ist keine Teilnehmer-Domain eingetragen.</Notice>
      ) : null}

      {view.enabled && url !== null ? (
        <div className="grid max-w-2xl gap-1.5">
          <Label htmlFor={`${id}-url`}>Link für Interessenten</Label>
          <Input
            id={`${id}-url`}
            className="bg-background font-mono"
            readOnly
            value={url}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
          />
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                navigator.clipboard
                  .writeText(url)
                  .then(() => {
                    setCopied("Link kopiert.");
                  })
                  .catch((error: unknown) => {
                    console.error("[ExamplePage] copy failed", error);
                    setCopied("Kopieren nicht möglich. Bitte den Link markieren und kopieren.");
                  });
              }}
            >
              Link kopieren
            </Button>
            <a
              className="text-sm underline underline-offset-4"
              href={url}
              target="_blank"
              rel="noopener"
            >
              Beispielansicht öffnen
            </a>
          </div>
          {copied === null ? null : (
            <p className="text-sm" role="status">
              {copied}
            </p>
          )}
          {/* The example in the article of a magazine website (D36, D39): its own frame code,
              next to the frames of the real calendar in the action's settings. */}
          <div className="mt-6 grid gap-6">
            <CopyField
              id={`${id}-shortcode`}
              label="WordPress: Shortcode für die Beispielansicht"
              hint="Läuft nur auf den Websites, die in den Einstellungen unter »Website« eingetragen sind. Im Rahmen gilt die Gestaltung »Auf Websites« der Aktion."
              value={embedShortcode({ calendarUrl: url, title: "Beispielansicht des Kalenders" })}
            />
            <CopyField
              id={`${id}-rahmen`}
              label="Andere Websites: Beispielansicht im Rahmen"
              hint="Fester Rahmen ohne Skript. Lädt die Website das Skript des Kalenders (Rahmen und Skript unter »Website«), wächst er mit."
              value={embedFrame({ calendarUrl: url, title: "Beispielansicht des Kalenders" })}
              multiline
            />
          </div>
        </div>
      ) : null}

      {view.enabled ? (
        <div className="mt-4 flex flex-wrap gap-6">
          <ConfirmButton
            label="Beispielansicht abschalten"
            question="Beispielansicht abschalten?"
            consequence="Der Link funktioniert danach nicht mehr, bis die Beispielansicht wieder eingeschaltet wird."
            confirmLabel="Abschalten"
            run={() => switchLink(false)}
            onDone={() => undefined}
          />
        </div>
      ) : null}

      {view.terms_published ? null : (
        <Notice tone="info" className="mt-6">
          Für diese Aktion sind noch keine Teilnahmebedingungen veröffentlicht. Die
          Beispielansicht endet dann vor dem Teilnahmeformular.
        </Notice>
      )}
    </Section>
  );
}

/** The open door, the partner mode and one row per door. */
function PartnerForm({
  view,
  actionId,
  fieldCount,
  mayAcceptUnscanned,
  took,
}: {
  readonly view: ExampleEdit;
  readonly actionId: string;
  readonly fieldCount: number;
  readonly mayAcceptUnscanned: boolean;
  readonly took: (outcome: ExampleOutcome) => ExampleOutcome;
}): ReactElement {
  const id = useId();
  // The rows as first loaded; names and checkboxes are typed here and sent with "Speichern".
  const [rows] = useState(() => partnerRows(view.partners, fieldCount));
  const [names, setNames] = useState<Readonly<Record<number, string>>>(() =>
    Object.fromEntries(rows.map((row) => [row.position, row.name])),
  );
  // The logos as last stored: an upload changes its own row only.
  const logos = new Map(partnerRows(view.partners, fieldCount).map((row) => [row.position, row]));
  const doors = Array.from({ length: fieldCount }, (_, index) => index + 1);

  return (
    <SaveForm
      label="Türchen und Partner"
      readOnly={false}
      onSaved={() => undefined}
      submit={async (form) =>
        asAction(took(await saveExampleSettings(actionId, settingsRequest(form, fieldCount))))
      }
    >
      <div className="grid max-w-xs gap-1.5">
        <Label htmlFor={`${id}-tuer`}>Heutiges Türchen</Label>
        <select
          id={`${id}-tuer`}
          name="booked_position"
          className="h-9 border border-input bg-background px-3 text-sm"
          defaultValue={view.booked_position}
          aria-describedby={`${id}-tuer-hinweis`}
        >
          {doors.map((position) => (
            <option key={position} value={position}>
              Türchen {position}
            </option>
          ))}
        </select>
        <p id={`${id}-tuer-hinweis`} className="text-sm text-muted-foreground">
          Türchen davor erscheinen als vergangen und lassen sich öffnen.
        </p>
      </div>

      <fieldset className="mt-6" aria-describedby={`${id}-hinweis`}>
        <legend className="text-sm font-medium">Logos auf den übrigen Türchen</legend>
        {(
          [
            ["list", "Beispielpartner aus der Liste"],
            ["real", "Echte Sponsoren dieser Aktion, sobald ihr Beitrag freigegeben ist"],
            ["none", "Keine Partner"],
          ] as const
        ).map(([mode, label]) => (
          <div key={mode} className="mt-2 flex items-center gap-2">
            <input
              id={`${id}-${mode}`}
              type="radio"
              name="partners_mode"
              value={mode}
              defaultChecked={view.partners_mode === mode}
            />
            <label className="text-sm" htmlFor={`${id}-${mode}`}>
              {label}
            </label>
          </div>
        ))}
        <p id={`${id}-hinweis`} className="mt-2 text-sm text-muted-foreground">
          Türchen ohne Partner zeigen das Bild für unbelegte Türchen aus der Gestaltung, ohne
          eigenes Bild das Markenlogo.
        </p>
      </fieldset>

      <h3 className="mt-6 text-base font-bold">Beispielpartner</h3>
      <p id={`${id}-logo-hinweis`} className="mt-1 text-sm text-muted-foreground">
        PNG, JPEG, WebP oder SVG, höchstens 2 MB. Der Name dient auch als Beschreibung für
        Screenreader.
      </p>
      <Table className="mt-3">
        <TableHeader>
          <TableRow>
            <TableHead>Türchen</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Logo</TableHead>
            <TableHead>Zeigen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const name = names[row.position] ?? "";
            const nameId = `${id}-${partnerInput("name", row.position)}`;
            const shownId = `${id}-${partnerInput("shown", row.position)}`;
            return (
              <TableRow key={row.position}>
                <TableCell className="align-top">{row.position}</TableCell>
                <TableCell className="align-top">
                  <label className="sr-only" htmlFor={nameId}>
                    Türchen {row.position}: Name
                  </label>
                  <Input
                    id={nameId}
                    name={partnerInput("name", row.position)}
                    className="min-w-40 bg-background"
                    maxLength={200}
                    value={name}
                    onChange={(event) => {
                      const value = event.target.value;
                      setNames((current) => ({ ...current, [row.position]: value }));
                    }}
                  />
                </TableCell>
                <TableCell className="align-top">
                  <PartnerLogo
                    row={logos.get(row.position) ?? row}
                    name={name.trim()}
                    actionId={actionId}
                    mayAcceptUnscanned={mayAcceptUnscanned}
                    hint={`${id}-logo-hinweis`}
                    took={took}
                  />
                </TableCell>
                <TableCell className="align-top">
                  <input
                    id={shownId}
                    type="checkbox"
                    name={partnerInput("shown", row.position)}
                    defaultChecked={row.shown}
                  />
                  <label className="sr-only" htmlFor={shownId}>
                    Türchen {row.position}: zeigen
                  </label>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </SaveForm>
  );
}

/**
 * The logo of one partner: a button of its own, never a nested form, so the names typed into
 * the table stay where they are. A file the security check could not clear can be taken by a
 * tenant admin with a reason.
 */
function PartnerLogo({
  row,
  name,
  actionId,
  mayAcceptUnscanned,
  hint,
  took,
}: {
  readonly row: PartnerRow;
  readonly name: string;
  readonly actionId: string;
  readonly mayAcceptUnscanned: boolean;
  readonly hint: string;
  readonly took: (outcome: ExampleOutcome) => ExampleOutcome;
}): ReactElement {
  const id = useId();
  const [file, setFile] = useState<File | null>(null);
  // Bumped after an upload: a file input cannot be emptied through its value.
  const [chooser, setChooser] = useState(0);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ readonly ok: boolean; readonly message: string } | null>(
    null,
  );
  const [unscanned, setUnscanned] = useState(false);
  const verb = row.logo === null ? "Logo hochladen" : "Logo ersetzen";

  const send = (unscannedReason?: string): void => {
    if (file === null) {
      setSaid({ ok: false, message: "Bitte zuerst eine Bilddatei wählen." });
      return;
    }
    setBusy(true);
    setSaid(null);
    uploadExamplePartnerLogo(actionId, {
      position: row.position,
      name,
      file,
      altText: name,
      unscannedReason,
    })
      .then((outcome) => {
        took(outcome);
        setUnscanned(!outcome.ok && outcome.unscanned === true);
        if (outcome.ok) {
          setFile(null);
          setChooser((value) => value + 1);
          setSaid({ ok: true, message: "Logo hochgeladen." });
        } else {
          setSaid({ ok: false, message: outcome.message });
        }
      })
      .catch((error: unknown) => {
        console.error("[ExamplePage] partner logo failed", error);
        setSaid({ ok: false, message: LOAD_FAILED });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className="grid min-w-48 gap-2">
      {row.logo === null ? (
        <p className="text-sm text-muted-foreground">Kein Logo</p>
      ) : (
        <>
          <img
            className="h-12 w-auto max-w-32 border border-border object-contain"
            src={mediaSrc(row.logo.path)}
            alt={row.logo.alt_text}
          />
          <CropButton
            assetId={assetIdOf(row.logo.path)}
            alt={row.logo.alt_text}
            label={`Türchen ${String(row.position)}: Logo zuschneiden`}
            disabled={busy || name === ""}
            onApply={async (crop) =>
              took(
                await uploadExamplePartnerLogo(actionId, {
                  position: row.position,
                  name,
                  assetId: assetIdOf(row.logo?.path ?? ""),
                  crop,
                }),
              )
            }
          />
        </>
      )}
      <FileDrop
        key={chooser}
        id={`${id}-datei`}
        compact
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        prompt="Logo auswählen"
        label={`Türchen ${String(row.position)}: Logodatei`}
        describedBy={hint}
        onChange={(chosen) => {
          setFile(chosen);
          setSaid(null);
          setUnscanned(false);
        }}
      />
      {name === "" ? (
        <p className="text-sm text-muted-foreground">Bitte zuerst den Namen eintragen.</p>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-max"
        disabled={busy || name === ""}
        aria-label={`Türchen ${String(row.position)}: ${verb}`}
        onClick={() => {
          send();
        }}
      >
        {verb}
      </Button>
      {said === null ? null : (
        <p className="text-sm" role={said.ok ? "status" : "alert"}>
          {said.message}
        </p>
      )}
      {mayAcceptUnscanned && unscanned ? (
        <UnscannedAccept
          busy={busy}
          onAccept={(reason) => {
            send(reason);
          }}
        />
      ) : null}
    </div>
  );
}
